import { Injectable } from '@angular/core';
// SA-8637: Sentry loaded from CDN (see src/index.html + src/types/sentry-cdn.d.ts).
import { environment } from '../../../environments/environment';

interface Script {
  name: string;
  src: string;
  loaded: boolean;
  promise?: Promise<void>;
}

interface ManifestShape {
  main?: string;
}

interface ResolvedUrl {
  url: string;
  source: 'manifest' | 'fallback';
}

@Injectable()
export class ScriptInjectorService {
  private scripts: Script[] = [];
  // SA-8595: in-flight + resolved manifest URL cache keyed by MFE name.
  // Concurrent first-callers share one fetch Promise.
  private resolvedUrls = new Map<string, Promise<ResolvedUrl>>();

  constructor() {}

  loadScript(name: string, fallbackUrl: string): Promise<void> {
    // Existing dedupe-by-name: if this script is already loaded or in flight,
    // return that Promise.
    const existingScript = this.scripts.find((s) => s.name === name);
    if (existingScript) {
      if (existingScript.loaded) {
        return Promise.resolve();
      }
      // The in-flight script's promise resolves when the <script> tag loads.
      return existingScript.promise ?? Promise.resolve();
    }
    // SA-8595: resolve via per-MFE manifest with in-memory cache + concurrent-
    // caller dedupe, then inject the <script> tag.
    return this.resolveUrl(name, fallbackUrl).then(({ url }) =>
      this.injectScript(name, url)
    );
  }

  private resolveUrl(name: string, fallbackUrl: string): Promise<ResolvedUrl> {
    const cached = this.resolvedUrls.get(name);
    if (cached) {
      return cached;
    }

    // The TS 3.8 root tsconfig doesn't know about `mfeCdnBaseUrl` on the
    // Environment shape, so we read via cast. Empty / undefined => skip
    // the manifest fetch and use the fallback URL directly.
    const baseUrl = (environment as any).mfeCdnBaseUrl as string | undefined;
    if (!baseUrl) {
      const direct: ResolvedUrl = { url: fallbackUrl, source: 'fallback' };
      const p = Promise.resolve(direct);
      this.resolvedUrls.set(name, p);
      return p;
    }

    const manifestUrl = `${baseUrl}/${name}.manifest.json`;
    const promise: Promise<ResolvedUrl> = fetch(manifestUrl, {
      cache: 'no-store'
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`manifest fetch failed: HTTP ${res.status}`);
        }
        return res.json();
      })
      .then((json: ManifestShape) => {
        if (!json || typeof json.main !== 'string' || json.main.length === 0) {
          throw new Error(
            `manifest missing 'main' field: ${JSON.stringify(json)}`
          );
        }
        return { url: `${baseUrl}/${json.main}`, source: 'manifest' as const };
      })
      .catch((err) => {
        console.error(
          `ScriptInjectorService: manifest fetch failed for ${name}, ` +
            `falling back to legacy URL.`,
          { manifestUrl, fallbackUrl, error: err }
        );
        return { url: fallbackUrl, source: 'fallback' as const };
      });

    this.resolvedUrls.set(name, promise);
    return promise;
  }

  private injectScript(name: string, src: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const scriptElement = document.createElement('script');
      scriptElement.type = 'text/javascript';
      scriptElement.src = src;
      scriptElement.async = true;
      const script: Script = {
        name,
        src,
        loaded: false
      };
      // SA-8595: capture the load outcome on `script.promise` so future
      // in-flight callers (via the dedupe-by-name path in loadScript) can
      // await this load instead of re-injecting a duplicate <script>.
      const promise = new Promise<void>((res, rej) => {
        scriptElement.onload = () => {
          script.loaded = true;
          res();
          resolve();
        };
        scriptElement.onerror = (
          event: Event | string,
          srcAttr?: string,
          lineno?: number,
          colno?: number,
          error?: Error
        ) => {
          const loadError = new Error(`Failed to load script: ${name}`);
          // SA-8637: defensive guard — see sentry.utils.ts.
          if (typeof Sentry !== 'undefined') {
            Sentry.captureException(loadError, {
              tags: { source: 'script-loader', mfe: name },
              extra: { src }
            });
          }
          console.error(loadError);
          rej(loadError);
          reject(loadError);
        };
      });
      script.promise = promise;
      document.body.appendChild(scriptElement);
      this.scripts.push(script);
    });
  }
}
